import { DEFAULT_LOCALE, ANALYTICS_EVENTS } from '@syncanix/shared';
import { createRoot, hydrateRoot } from 'react-dom/client';

import { track } from './analytics/track';
import { buildClientTree } from './AppTree';
import { groupsForPath } from './i18n/catalog-groups';
import { catalogStore } from './i18n/catalog-store';
import { localeFromPath } from './i18n/locale-path';
import { preloadForPath } from './routes';

import type { Translator } from './i18n/translator';
// `@syncanix/ui` ships design tokens via its `styles.css`. We import it from JS
// rather than via CSS `@import` because Vite's PostCSS pipeline doesn't resolve
// npm-package specifiers in `@import` without the optional `postcss-import`
// plugin (not installed here).
import '@syncanix/ui/styles.css';
// Geist Sans + Geist Mono variable fonts (the family the `@syncanix/ui`
// Tailwind preset aliases as `font-sans`/`font-mono`). apps/web loads these
// via its own entry; marketing did not until the landing redesign needed them
// for pixel fidelity — they also upgrade the rest of the site off the system
// fallback. Imported from JS (not CSS `@import`) per the styles.css note.
import '@fontsource-variable/geist';
import '@fontsource-variable/geist-mono';
import './styles/globals.css';
// Bedrock tokens + ambient motion — the design-system foundation every
// marketing surface composes from. After globals.css so its `:root` block
// wins over the legacy `--sx-*` aliases declared there.
import './components/bedrock/bedrock.css';

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}
// Capture the narrowed (non-null) element so the mount closure below sees a
// concrete HTMLElement (control-flow narrowing of `rootEl` does not propagate
// into the nested function).
const rootElement: HTMLElement = rootEl;

// Per-locale documentation URLs (ADR-0075) carry their locale in the path. For a
// non-English docs page, the prerendered markup is in-language, so the first client
// render must be too — await that locale's catalog and seed the provider with it
// before hydrating (otherwise React would see an EN-vs-locale hydration mismatch).
// English pages keep the original detect-from-browser path (initialLocale omitted).
const urlLocale = localeFromPath(window.location.pathname);

function mount(initialTranslator?: Translator): void {
  // The tree MUST be structurally identical to the server's (useId parity) —
  // both sides build from the shared AppTree helpers; only the router differs.
  const app = buildClientTree({
    locale: urlLocale !== DEFAULT_LOCALE ? urlLocale : undefined,
    translator: initialTranslator,
    seoSink: null,
  });
  if (rootElement.childElementCount > 0) {
    // Production: the prerendered (SSG) markup is present — hydrate it.
    // `childElementCount` (not `hasChildNodes`) so stray whitespace text nodes in
    // index.html don't make dev mistake an empty shell for prerendered markup.
    hydrateRoot(rootElement, app, {
      // B-AUDIT-1 — hydration mismatches were operator-invisible (minified
      // #418/#423 in the visitor's console only). Surface every recoverable
      // hydration error through the same PostHog rail as render crashes,
      // WITH React's component stack — in production builds too.
      onRecoverableError: (error, errorInfo) => {
        try {
          const message = error instanceof Error ? error.message : String(error);
          track(ANALYTICS_EVENTS.MARKETING_ERROR_BOUNDARY_CAUGHT, {
            message: `hydration: ${message}`.slice(0, 300),
            componentStack: (errorInfo.componentStack ?? '').slice(0, 600),
            path: window.location.pathname,
          });
          // Keep the default console visibility (React stops logging once a
          // custom handler exists) — operators debugging locally still see it.
          console.error(
            '[marketing] recoverable hydration error',
            message,
            errorInfo.componentStack,
          );
        } catch {
          // Reporting is best-effort by definition.
        }
      },
    });
  } else {
    // Dev (empty `#root`, no prerender) — client-render.
    createRoot(rootElement).render(app);
  }
}

// Catalog groups (ADR-0195): resolve exactly the pieces this document renders
// — the landing page loads `core` (~10% of the old monolithic catalog), docs
// pages add `docs`, other routes add `pages` — BEFORE hydrating, so the first
// client render matches the in-language prerendered markup. Called before
// `preloadForPath` so the store's active locale is set when the lazy-route
// factories request their own groups.
const catalogReady: Promise<Translator | undefined> = catalogStore
  .ensureCatalog(urlLocale, groupsForPath(window.location.pathname))
  .catch(() => undefined);

// B2 code splitting: resolve the active route's chunk BEFORE hydrating —
// renderToString emits no Suspense markers, so suspending during hydration
// would discard the server HTML (React #418/#423). A failed preload still
// mounts (the Suspense fallback + client render is the graceful path).
const routeReady: Promise<void> = preloadForPath(window.location.pathname).catch(() => undefined);

// The catalog fetch races the route-chunk fetch; both must land pre-mount.
void Promise.all([catalogReady, routeReady]).then(([translator]) => {
  mount(translator ?? undefined);
});
